import type { JsonDataItem } from '@myosh/odin-components';
import { Checkbox, Combobox, ComboboxActions, cn, useDebouncedValue } from '@myosh/odin-components';
import type React from 'react';
import { memo, useMemo, useState } from 'react';
import { useGetAllBowtiesQuery, useGetBowtieByIdQuery } from '../../api/enhanced/enhanced-bowtie-api';
import { useGetSimpleRecordQuery, useGetSimpleRecordsPagedInfiniteQuery } from '../../api/enhanced/enhanced-v4-api';
import { useConfigurationContext } from '../../context/configuration.context';
import { useDiagramContext } from '../../context/diagram.context';

const NEW_DIAGRAM_PLACEHOLDER = 'New Diagram';

const valueFieldKey = 'id';
const textFieldKey = 'displayText';

const QUERY_DEBOUNCE_MS = 300;

/**
 * A dropdown component for the header section that displays and manages record selection.
 *
 * @component
 * @description
 * Renders a dropdown menu that allows users to:
 * - Display currently selected record
 * - Search and select records from an API
 * - Open selected records in a new window
 *
 * The component integrates with URL parameters to maintain state and uses the Combobox
 * component for the actual dropdown functionality.
 *
 * @example
 * ```tsx
 * <RecordDropDown />
 * ```
 *
 * @remarks
 * The component handles two main modes:
 * - Regular records mode (when `isSavedDiagram` is false)
 * - Saved diagrams mode (when `isSavedDiagram` is true)
 *
 * @returns {JSX.Element} A dropdown component wrapped in a fixed-width container
 */

const RecordDropDown = (): React.JSX.Element => {
  const { isNewDiagram, isSavedDiagram, recordId, diagramId } = useDiagramContext();

  const [record, setRecord] = useState<JsonDataItem>();
  const [showDraft, setShowDraft] = useState<boolean>(isSavedDiagram);

  const {
    main: { formId, scenarioFieldId },
  } = useConfigurationContext();

  const [query, setQuery] = useState('');
  const debouncedQuery = useDebouncedValue(query, QUERY_DEBOUNCE_MS);

  const { data, isFetching } = useGetSimpleRecordQuery(
    { id: Number(recordId), params: {} },
    { skip: !recordId || isNewDiagram || showDraft }
  );

  const { data: diagram, isFetching: isDiagramFetching } = useGetBowtieByIdQuery(
    { bowtieId: diagramId! },
    { skip: !diagramId || isNewDiagram }
  );

  if (!isFetching && data && !record && !showDraft) {
    setRecord({ ...data, id: data.id, displayText: data.fields?.[scenarioFieldId] });
  }

  if (!isDiagramFetching && diagram && !record && showDraft) {
    setRecord({ ...diagram, id: diagram.id, displayText: diagram.risk });
  }

  const { data: diagrams, isFetching: isDiagramsFetching } = useGetAllBowtiesQuery(undefined, { skip: !showDraft });

  const {
    data: recordPages,
    isFetching: isRecordsFetching,
    hasNextPage,
    fetchNextPage,
  } = useGetSimpleRecordsPagedInfiniteQuery(
    {
      formId,
      sortDirective: [`field:${scenarioFieldId}`],
      filter: debouncedQuery ? `field:${scenarioFieldId}:like:${encodeURIComponent(debouncedQuery)}` : undefined,
    },
    { skip: showDraft }
  );

  const diagramItems = useMemo(() => {
    const mapped = (diagrams ?? []).map((diagram) => ({
      ...diagram,
      [valueFieldKey]: diagram.id,
      [textFieldKey]: diagram.risk,
    }));
    const search = query.trim().toLocaleLowerCase();
    return search
      ? mapped.filter((diagram) =>
          String(diagram[textFieldKey] ?? '')
            .toLocaleLowerCase()
            .includes(search)
        )
      : mapped;
  }, [diagrams, query]);

  const recordItems = useMemo(
    () =>
      (recordPages?.pages ?? [])
        .flat()
        .map((record) => ({ ...record, [textFieldKey]: record.fields?.[scenarioFieldId] })),
    [recordPages, scenarioFieldId]
  );

  const items = showDraft ? diagramItems : recordItems;
  const isLoading = showDraft ? isDiagramsFetching : isRecordsFetching;
  // Diagrams arrive in a single response, so there is never more to load on that branch.
  const hasMore = showDraft ? false : hasNextPage;
  const onLoadMore = () => {
    if (!showDraft && hasNextPage && !isRecordsFetching) {
      void fetchNextPage();
    }
  };

  // Selecting a record/diagram is a navigation action (opens it in a new window); the trigger keeps showing the
  // current record, so the controlled value is left unchanged. A null value means the user emptied the search box,
  // which resets the filter rather than deselecting.
  const handleOnChange = (value: JsonDataItem | null) => {
    if (!value) {
      return;
    }

    const id = String(value[valueFieldKey]);
    const params = showDraft ? new URLSearchParams({ diagramId: id }) : new URLSearchParams({ records: id });
    window.open(`${window.location.origin}${window.location.pathname}?${params}`);
  };

  return (
    <Combobox
      items={items}
      value={record ?? null}
      placeholder={isNewDiagram ? NEW_DIAGRAM_PLACEHOLDER : undefined}
      onValueChange={handleOnChange}
      getKey={(item) => String(item[valueFieldKey])}
      getLabel={(item) => String(item[textFieldKey] ?? '')}
      isLoading={isLoading}
      hasMore={hasMore}
      onLoadMore={onLoadMore}
      query={query}
      onQueryChange={setQuery}
      className={cn(
        'min-w-80 max-w-80 py-6',
        // Title-weight trigger text
        '**:data-[slot=input-group-control]:font-semibold **:data-[slot=input-group-control]:text-lg **:data-[slot=input-group-control]:leading-7',
        // The placeholder stands in for a record name, so it matches that text rather than reading as unset
        '**:data-[slot=input-group-control]:placeholder:text-inherit'
      )}
    >
      <ComboboxActions align="block-start">
        <Checkbox label="Show Draft" checked={showDraft} onChange={(event) => setShowDraft(event.checked)} />
      </ComboboxActions>
    </Combobox>
  );
};

export default memo(RecordDropDown);
